import { Box, Container, Flex, HStack, Image, Link } from '@chakra-ui/react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CtaButton } from './CtaButton';

const NAV_LINKS = [
  { key: 'howItWorks', id: 'how-it-works' },
  { key: 'pricing', id: 'pricing' },
  { key: 'faq', id: 'faq' },
  { key: 'contacts', to: '/contacts' },
] as const;

/**
 * Sticky marketing header: logo, anchor nav, Login, primary CTA.
 * Translucent dark bar so it reads over the hero gradient. (Mobile drawer +
 * language switch are refined in US4.)
 */
export function LandingHeader() {
  const { t } = useTranslation();
  // Anchor nav targets only exist on the home route; off-route, jump home + hash.
  const onHome = useLocation().pathname === '/';
  const anchor = (id: string) => (onHome ? `#${id}` : `/#${id}`);
  return (
    <Box
      as="header"
      position="sticky"
      top={0}
      zIndex={30}
      bg="rgba(19,7,13,0.6)"
      backdropFilter="blur(12px)"
    >
      <Container maxW="1440px" px={{ base: 5, xl: '50px' }}>
        <Flex align="center" justify="space-between" h={{ base: '68px', xl: '88px' }}>
          <Box as="a" href={onHome ? '#hero' : '/'} aria-label="Streamko" display="inline-flex">
            <Image src="/images/landing/cmp-logo.png" alt="Streamko" h={{ base: '24px', xl: '30px' }} draggable={false} />
          </Box>

          <HStack as="nav" spacing="48px" display={{ base: 'none', lg: 'flex' }}>
            {NAV_LINKS.map((l) => (
              <Link
                key={l.key}
                href={'to' in l ? l.to : anchor(l.id)}
                fontSize="sm"
                fontWeight={400}
                color="whiteAlpha.900"
                _hover={{ color: 'white', textDecoration: 'none' }}
              >
                {t(`landing.nav.${l.key}`)}
              </Link>
            ))}
          </HStack>

          <HStack spacing={{ base: 3, md: 6 }}>
            <Link
              as={RouterLink}
              to="/signup"
              display={{ base: 'none', md: 'inline-flex' }}
              alignItems="center"
              gap="8px"
              fontSize="md"
              fontWeight={600}
              color="white"
              _hover={{ textDecoration: 'none', opacity: 0.85 }}
            >
              <Box
                as="svg"
                viewBox="0 0 24 24"
                w="18px"
                h="18px"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                <polyline points="10 17 15 12 10 7" />
                <line x1="15" y1="12" x2="3" y2="12" />
              </Box>
              {t('landing.nav.login')}
            </Link>
            <CtaButton
              to="/onboarding"
              bg="onboarding.cream"
              color="onboarding.plum"
              borderRadius="pill"
              height="44px"
              px="24px"
              fontSize={{ base: 'sm', md: 'md' }}
              fontWeight={600}
              _hover={{ bg: 'white' }}
              trackEventName="cta_click"
              trackProps={{ section: 'nav' }}
            >
              {t('landing.nav.startTrial')}
            </CtaButton>
          </HStack>
        </Flex>
      </Container>
    </Box>
  );
}
